import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';
import { HOME_PATH, PAYMENT_FAIL_ORDER_PARAM, paymentCheckoutPath } from '@/shared/config';
import { buttonVariants, cn, EyebrowLabel, SitePageShell } from '@/shared/ui';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

/** 버튼 줄의 버튼 — 휴대폰에서는 전폭으로 쌓이고, 넓은 화면(sm)에서는 한 줄에 내용 폭(최소 12rem)으로 놓인다. */
const ACTION_CLASS = 'h-14 w-full text-base sm:w-auto sm:min-w-48';

/**
 * 결제 실패 페이지 — 토스가 넘긴 code·message 를 안내만 한다(결제 설계 §4-4).
 * 결과 화면처럼 한 칸이다: 머리 아래 읽기 폭(max-w-2xl) 칸에 경고(문구·오류 코드)를 두고, 바로 아래 버튼 줄
 * (같은 주문 다시 결제하기·홈으로)을 둔다. 우리 주문 ID 가 없으면 홈으로만 둔다.
 */
export const PaymentFailPage = (): ReactElement => {
  const [params] = useSearchParams();
  const message = params.get('message') ?? '결제가 완료되지 않았습니다.';
  const code = params.get('code');
  const orderId = params.get(PAYMENT_FAIL_ORDER_PARAM);

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
      <div className="flex flex-col gap-10 sm:gap-12">
        <header className="flex flex-col gap-4 border-b border-foreground/10 pb-8">
          <EyebrowLabel>Payment</EyebrowLabel>
          <h1 className="text-[clamp(2.25rem,4.5vw,4rem)] leading-none font-bold tracking-[-0.03em]">
            결제 실패
          </h1>
        </header>

        <div className="flex max-w-2xl flex-col gap-6">
          <div className="flex flex-col gap-2 border-l-2 border-destructive bg-destructive/5 px-5 py-4">
            <p role="alert" className="text-sm font-semibold text-destructive">
              {message}
            </p>
            {code ? (
              <p className="text-xs text-muted-foreground wrap-anywhere">오류 코드: {code}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            {orderId ? (
              <Link
                to={paymentCheckoutPath(orderId)}
                className={cn(buttonVariants({ size: 'lg' }), ACTION_CLASS)}
              >
                다시 결제하기
              </Link>
            ) : null}
            <Link
              to={HOME_PATH}
              className={cn(
                buttonVariants({ variant: 'outline', size: 'lg' }),
                ACTION_CLASS,
                'border-foreground/20',
              )}
            >
              홈으로
            </Link>
          </div>
        </div>
      </div>
    </SitePageShell>
  );
};
